import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextareaAutosize,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  Checkbox,
} from '@mui/material';
import {
  insert,
  notifyByEmail,
  postEvidenceInOikos,
  getLinkEvidencia,
  downloadFile,
  getLinksDocumentoDeRevisaoInterna,
  getRegs,
  getProdCoordenadorPfId,
} from '../../../utils/fc/index.js';
import Dropzone from 'react-dropzone';
import { Spinner } from 'reactstrap';
import oikosApi from '../../../services/oikos/index.js';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { currencyFormatter } from '../../../utils/currencyFormatter';
import ModalLiberacaoFaturamentoRec from '../../Template/Doc/ModalLiberacaoFaturamentoRec';
import { uploadFileToDocumento } from '../../../utils/oikosUploadDocumento';
import { TP_DOC_REC_LIBERACAO_FATURAMENTO } from '../../../constants/oikosReceitas';
import { isRecAutorizado } from '../../../utils/recLiberacaoFaturamento';

const PROD_STATUS_TP_FATURAMENTO_AUTORIZADO = 8;

const formatEventoPagamentoLabel = (r) => {
  const parcLabel =
    r.rec_parc != null && r.rec_parc !== ''
      ? `Parcela ${r.rec_parc}`
      : 'Sem nº de parcela';
  const ep = r.rec_ep ? String(r.rec_ep).trim() : '—';
  const vlr =
    r.rec_vlrBru != null && r.rec_vlrBru !== ''
      ? currencyFormatter.format(Number(r.rec_vlrBru))
      : '—';
  return `${parcLabel} — ${ep} — ${vlr}`;
};

const blockBackdropClose = (handler) => (event, reason) => {
  if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
  handler(event, reason);
};

const ModalFormAprovacaoDoCliente = ({ dataContratual, tecResp, actual, ...props }) => {
  const [open, setOpen] = useState(false);
  const [obsTec, setObsTec] = useState('');
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [linksRevisao, setLinksRevisao] = useState(null);
  const [wizardPhase, setWizardPhase] = useState('form');
  const [multiAlso, setMultiAlso] = useState(false);
  const [extraProdIds, setExtraProdIds] = useState([]);
  const [recList, setRecList] = useState([]);
  const [recGateChoice, setRecGateChoice] = useState('rec');
  const [selectedRecId, setSelectedRecId] = useState('');
  const [linkedRecId, setLinkedRecId] = useState(null);
  const [authFatChoice, setAuthFatChoice] = useState('no');
  const [libModalOpen, setLibModalOpen] = useState(false);
  const [libModalRec, setLibModalRec] = useState(null);

  const orcRubr_id = props.orcRubr_id;
  const projectProducts = props.projectProducts || [];

  const getLinksRevisao = async () => {
    const links = await getLinksDocumentoDeRevisaoInterna(props.produto.prod_id);
    setLinksRevisao(links);
  };

  useEffect(() => {
    getLinksRevisao();
  }, [props.produto.prod_id]);

  useEffect(() => {
    if (wizardPhase !== 'recGate' || !orcRubr_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const raw = await getRegs('rec', { orcRubr_id });
        const list = Array.isArray(raw) ? raw : [];
        if (!cancelled) setRecList(list);
      } catch (e) {
        console.log(e);
        if (!cancelled) setRecList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wizardPhase, orcRubr_id]);

  useEffect(() => {
    if (wizardPhase === 'recGate' && props.produto.rec_id) {
      setSelectedRecId(String(props.produto.rec_id));
    }
  }, [wizardPhase, props.produto.rec_id]);

  const resetAndClose = () => {
    setOpen(false);
    setWizardPhase('form');
    setMultiAlso(false);
    setExtraProdIds([]);
    setRecGateChoice('rec');
    setSelectedRecId('');
    setLinkedRecId(null);
    setAuthFatChoice('no');
    setLibModalOpen(false);
    setLibModalRec(null);
    setFiles([]);
    setObsTec('');
  };

  const handleClickOpen = () => {
    setOpen(true);
    setWizardPhase('form');
  };

  const handleClose = () => {
    if (isStatusLoading || libModalOpen) return;
    resetAndClose();
  };

  const refreshProductUi = () => {
    props.setCount((prev) => prev + 1);
    if (props.onProductTimelineChanged) props.onProductTimelineChanged();
  };

  const insertStatus8 = async (prodId) => {
    await insert('prodStatusAlt', {
      prod_id: prodId,
      prodStatusTp_id: PROD_STATUS_TP_FATURAMENTO_AUTORIZADO,
      pf_id: getProdCoordenadorPfId(props.produto),
      prodStatusAlt_obs: obsTec,
      prodStatusAlt_data: moment().format('YYYY-MM-DD'),
      prodStatusAlt_usuarioCria: props.userOikosId,
      prodStatusAlt_usuarioMod: props.userOikosId,
    });
  };

  const finishFaturamentoProduto = async (prodId, recRow) => {
    const dtFat = recRow?.rec_dtLibAutNf
      ? moment(recRow.rec_dtLibAutNf).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');
    await oikosApi.post('/', {
      action: 'update',
      table: 'prod',
      data: {
        prod_id: prodId,
        prod_dtAutFat: dtFat,
      },
    });
    await insertStatus8(prodId);
    const vlrLiberado =
      recRow?.rec_vlrLibAutNf != null && recRow.rec_vlrLibAutNf !== ''
        ? recRow.rec_vlrLibAutNf
        : recRow?.rec_vlrBru;
    await notifyByEmail(prodId, {
      obs: obsTec,
      vlrLiberado,
      dtAutFat: dtFat,
    });
  };

  const uploadApprovalFilesAsLib41 = async (recId) => {
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      await uploadFileToDocumento({
        tpDocumentoId: TP_DOC_REC_LIBERACAO_FATURAMENTO,
        tabRef: 'rec',
        tabId: recId,
        file: f.data,
        documentoDesc: `Liberação faturamento (evidência aprovação produto) [${i + 1}/${files.length}]`,
      });
    }
  };

  const completeWithFaturamentoAutorizado = async (recRow) => {
    try {
      await finishFaturamentoProduto(props.produto.prod_id, recRow);
      notifySuccess('Produto associado e faturamento autorizado.');
      refreshProductUi();
      resetAndClose();
    } catch (e) {
      console.log(e);
      notifyError(e?.message || 'Falha ao registrar autorização de faturamento no produto.');
    }
  };

  const proceedWithFaturamentoAuth = async (rid) => {
    const raw = await getRegs('rec', { rec_id: rid });
    const recRow = Array.isArray(raw) && raw[0] ? raw[0] : null;
    if (!recRow) {
      notifyError('Evento de pagamento não encontrado.');
      return;
    }
    if (isRecAutorizado(recRow)) {
      setIsStatusLoading(true);
      try {
        await uploadApprovalFilesAsLib41(rid);
      } catch (e) {
        console.log(e);
      }
      await completeWithFaturamentoAutorizado(recRow);
      setIsStatusLoading(false);
      return;
    }
    setLibModalRec(recRow);
    setLibModalOpen(true);
    setIsStatusLoading(false);
  };

  const onLiberacaoModalSuccess = async () => {
    const rid = libModalRec?.rec_id;
    setLibModalOpen(false);
    setLibModalRec(null);
    if (!rid) return;
    setIsStatusLoading(true);
    try {
      const raw = await getRegs('rec', { rec_id: rid });
      const row = Array.isArray(raw) && raw[0] ? raw[0] : null;
      if (!row) {
        notifyError('Não foi possível recarregar o evento de pagamento.');
        return;
      }
      await completeWithFaturamentoAutorizado(row);
    } finally {
      setIsStatusLoading(false);
    }
  };

  const finishWithoutFaturamentoAuth = () => {
    notifySuccess(
      'Produto associado ao evento de pagamento. A autorização de faturamento pode ser feita depois na aba Eventos de pagamento ou na timeline do produto.'
    );
    refreshProductUi();
    resetAndClose();
  };

  const handleAuthGateContinue = async () => {
    if (!linkedRecId) {
      notifyError('Nenhum evento de pagamento vinculado.');
      return;
    }
    if (authFatChoice === 'no') {
      finishWithoutFaturamentoAuth();
      return;
    }
    setIsStatusLoading(true);
    try {
      await proceedWithFaturamentoAuth(linkedRecId);
    } finally {
      if (!libModalOpen) setIsStatusLoading(false);
    }
  };

  const handleChangeObs = (event) => {
    setObsTec(event.target.value);
  };

  const handleRecGateContinue = async () => {
    if (recGateChoice === 'none') {
      try {
        await oikosApi.post('/', {
          action: 'update',
          table: 'prod',
          data: {
            prod_id: props.produto.prod_id,
            rec_id: null,
          },
        });
        notifySuccess('Aprovação registrada. Produto sem evento de pagamento associado.');
      } catch (e) {
        console.log(e);
        notifyError(e?.message || 'Falha ao atualizar o produto.');
        return;
      }
      refreshProductUi();
      resetAndClose();
      return;
    }
    const rid = Number(selectedRecId);
    if (!rid) {
      notifyError('Selecione um evento de pagamento ou indique que não há evento associado.');
      return;
    }
    setIsStatusLoading(true);
    try {
      await oikosApi.post('/', {
        action: 'update',
        table: 'prod',
        data: {
          prod_id: props.produto.prod_id,
          rec_id: rid,
        },
      });
      setLinkedRecId(rid);
      setAuthFatChoice('no');
      setWizardPhase('authGate');
    } catch (e) {
      console.log(e);
      notifyError(e?.message || 'Falha ao associar evento de pagamento.');
    } finally {
      setIsStatusLoading(false);
    }
  };

  const aprovaCliente = async () => {
    if (files.length <= 0) {
      const erroDrop = document.querySelector('.erro-drop');
      if (erroDrop) erroDrop.style.display = 'block';
      return;
    }
    setIsStatusLoading(true);
    try {
      const targets = [props.produto];
      if (multiAlso && extraProdIds.length > 0) {
        extraProdIds.forEach((id) => {
          const p = projectProducts.find((x) => Number(x.prod_id) === Number(id));
          if (p) targets.push(p);
        });
      }
      for (let t = 0; t < targets.length; t += 1) {
        const p = targets[t];
        await insert('prodStatusAlt', {
          prod_id: p.prod_id,
          prodStatusTp_id: 7,
          pf_id: getProdCoordenadorPfId(p),
          prodStatusAlt_obs: obsTec,
          prodStatusAlt_data: moment().format('YYYY-MM-DD'),
          prodStatusAlt_usuarioCria: props.userOikosId,
          prodStatusAlt_usuarioMod: props.userOikosId,
        });
        await postEvidenceInOikos(files, p);
        const linksEvidencias = await getLinkEvidencia(p.prod_id);
        await notifyByEmail(p.prod_id, { obs: obsTec, linksEvidencias });
      }
      notifySuccess('Produto aprovado!');
      if (!orcRubr_id) {
        props.setCount((prev) => prev + 1);
        if (props.onProductTimelineChanged) props.onProductTimelineChanged();
        resetAndClose();
        setIsStatusLoading(false);
        return;
      }
      setWizardPhase('recGate');
      setIsStatusLoading(false);
    } catch (e) {
      console.log(e);
      notifyError(e?.message || 'Falha na aprovação.');
      setIsStatusLoading(false);
    }
  };

  const reprovaCliente = async () => {
    if (files.length <= 0) {
      const erroDrop = document.querySelector('.erro-drop');
      if (erroDrop) erroDrop.style.display = 'block';
      return;
    }
    setIsStatusLoading(true);
    try {
      const insertedId = await insert('prodStatusAlt', {
        prod_id: props.produto.prod_id,
        prodStatusTp_id: 9,
        pf_id: getProdCoordenadorPfId(props.produto),
        prodStatusAlt_obs: obsTec,
        prodStatusAlt_data: moment().format('YYYY-MM-DD'),
        prodStatusAlt_usuarioCria: props.userOikosId,
        prodStatusAlt_usuarioMod: props.userOikosId,
      });
      if (!insertedId) {
        notifyError('Falha ao registrar a reprovação do cliente.');
        return;
      }
      await postEvidenceInOikos(files, props.produto);
      const linksEvidencias = await getLinkEvidencia(props.produto.prod_id);
      await notifyByEmail(props.produto.prod_id, { obs: obsTec, linksEvidencias });
      notifySuccess('Produto reprovado.');
      resetAndClose();
      props.setCount((prev) => prev + 1);
    } catch (e) {
      console.log(e);
      notifyError(e?.message || 'Falha na reprovação.');
    } finally {
      setIsStatusLoading(false);
    }
  };

  const formatFileIcon = (type) => {
    switch (type) {
      case 'application/pdf':
        return {
          color: '#ffcfcc',
          labelColor: '#FF2116',
          type: 'acrobat',
        };
      case 'image/png':
      case 'image/jpeg':
      case 'image/gif':
        return {
          color: '#ffe6cc',
          labelColor: '#ff9933',
          type: 'image',
        };
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return {
          color: '#d7f4e4',
          labelColor: '#207245',
          type: 'spreadsheet',
        };
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return {
          color: '#d7e3f4',
          labelColor: '#2a5699',
          type: 'document',
        };
      default:
        return {};
    }
  };

  const extractFileExtension = (path) => {
    const fileExtensionPattern = /\.([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/gim;
    return path.match(fileExtensionPattern)[0].substring(1);
  };

  const handleOnDropFiles = (newAcceptedFiles) => {
    const acceptedFormatted = newAcceptedFiles?.map((file) => {
      const icon = formatFileIcon(file.type);
      const extension = extractFileExtension(file.path);
      const name = file.path.replace(/\.[^/.]+$/, '');
      return {
        data: file,
        props: {
          id: uuidv4(),
          preview: URL.createObjectURL(file),
          modified: moment().format('DD/MM/YYYY HH:mm'),
          icon,
          extension,
          name,
          documentType: null,
          description: null,
        },
      };
    });
    setFiles((prev) => prev.concat(acceptedFormatted));
  };

  const handleDeleteFile = (id) => {
    setFiles((prevFiles) => prevFiles.filter((fileWrapper) => fileWrapper.props.id !== id));
  };

  const extraOptions = projectProducts.filter((p) => Number(p.prod_id) !== Number(props.produto.prod_id));

  const showApprovalTrigger = Number(actual) === 6;

  const dialogTitle =
    wizardPhase === 'form'
      ? 'Aprovação do cliente'
      : wizardPhase === 'authGate'
        ? 'Autorização de faturamento'
        : 'Associar produto a faturamento';

  return (
    <>
      {showApprovalTrigger && (
        <Button
          size="small"
          variant="text"
          sx={{
            fontSize: 11,
            textTransform: 'none',
            lineHeight: 1.2,
            maxWidth: 96,
            whiteSpace: 'normal',
            p: '2px 4px',
          }}
          onClick={handleClickOpen}
        >
          Aprovação do cliente
        </Button>
      )}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={blockBackdropClose(handleClose)}
        disableEscapeKeyDown
        PaperProps={{
          component: 'form',
          onSubmit: async (event) => {
            event.preventDefault();
          },
        }}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {wizardPhase === 'recGate' && (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Vincule este produto a um evento de pagamento do projeto, se houver. Na etapa seguinte você
                poderá indicar se esta aprovação também autoriza o faturamento desse evento.
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={recGateChoice}
                  onChange={(e) => setRecGateChoice(e.target.value)}
                >
                  <FormControlLabel value="rec" control={<Radio />} label="Associar evento de pagamento do projeto" />
                  <FormControlLabel value="none" control={<Radio />} label="Não há evento de pagamento para este produto" />
                </RadioGroup>
              </FormControl>
              {recGateChoice === 'rec' && (
                <FormControl fullWidth sx={{ mt: 2 }} disabled={!recList.length}>
                  <InputLabel id="rec-select-label">Evento de pagamento</InputLabel>
                  <Select
                    labelId="rec-select-label"
                    value={selectedRecId}
                    label="Evento de pagamento"
                    onChange={(e) => setSelectedRecId(e.target.value)}
                  >
                    {recList.map((r) => (
                      <MenuItem key={r.rec_id} value={String(r.rec_id)}>
                        {formatEventoPagamentoLabel(r)}
                      </MenuItem>
                    ))}
                  </Select>
                  {!recList.length && (
                    <Typography variant="caption" color="error">
                      Nenhum evento encontrado para este orçamento. Use a opção sem evento ou cadastre na aba Eventos de pagamento.
                    </Typography>
                  )}
                </FormControl>
              )}
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="outlined" onClick={() => setWizardPhase('form')} disabled={isStatusLoading}>
                  Voltar
                </Button>
                <Button variant="contained" onClick={handleRecGateContinue} disabled={isStatusLoading}>
                  {isStatusLoading ? 'Salvando…' : 'Associar'}
                </Button>
                <Button onClick={handleClose} disabled={isStatusLoading}>
                  Cancelar
                </Button>
              </Box>
            </Box>
          )}

          {wizardPhase === 'authGate' && (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                O produto já está vinculado ao evento de pagamento selecionado. A aprovação do cliente também
                vale como autorização de faturamento para esse evento?
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={authFatChoice}
                  onChange={(e) => setAuthFatChoice(e.target.value)}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Sim — autorizar faturamento agora"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="Não — autorizar depois (Eventos de pagamento ou timeline do produto)"
                  />
                </RadioGroup>
              </FormControl>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => setWizardPhase('recGate')} disabled={isStatusLoading}>
                  Voltar
                </Button>
                <Button variant="contained" onClick={handleAuthGateContinue} disabled={isStatusLoading}>
                  {isStatusLoading ? 'Aguarde…' : 'Continuar'}
                </Button>
                <Button onClick={handleClose} disabled={isStatusLoading}>
                  Cancelar
                </Button>
              </Box>
            </Box>
          )}

          {wizardPhase === 'form' && (
            <div className="d-flex">
              <div className="d-flex flex-column flex-fill">
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <span>Técnico Responsável</span>
                    <h6>{tecResp}</h6>
                  </div>
                  <div>
                    <span>Data contratual</span>
                    {moment(dataContratual, 'DD/MM/YYYY').toDate().getTime() > Date.now() ? (
                      <h6 className="text-success">{dataContratual}</h6>
                    ) : (
                      <h6 className="text-danger">{dataContratual}</h6>
                    )}
                  </div>
                  <div>
                    <span>Data Entrega ao EP</span>
                    {props.dataEntregueAGestao && !props.dataEntregueAoEp && moment(props.dataEntregueAGestao, 'DD/MM/YYYY').toDate().getTime() > new Date(props.produto.prod_dtInterna).getTime() && (
                      <h6 className="text-success">{props.dataEntregueAGestao}</h6>
                    )}
                    {props.dataEntregueAGestao && !props.dataEntregueAoEp && moment(props.dataEntregueAGestao, 'DD/MM/YYYY').toDate().getTime() < new Date(props.produto.prod_dtInterna).getTime() && (
                      <h6 className="text-danger">{props.dataEntregueAGestao}</h6>
                    )}
                    {props.dataEntregueAoEp && moment(props.dataEntregueAoEp, 'DD/MM/YYYY').toDate().getTime() > new Date(props.produto.prod_dtInterna).getTime() && (
                      <h6 className="text-success">{props.dataEntregueAoEp}</h6>
                    )}
                    {props.dataEntregueAoEp && moment(props.dataEntregueAoEp, 'DD/MM/YYYY').toDate().getTime() < new Date(props.produto.prod_dtInterna).getTime() && (
                      <h6 className="text-danger">{props.dataEntregueAoEp}</h6>
                    )}
                  </div>
                </div>
                <span>Selecione a evidência:</span>
                <Dropzone maxSize={100000000} onDrop={(dropped) => handleOnDropFiles(dropped)}>
                  {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, isFocused }) => {
                    const style = {
                      ...baseStyle,
                      ...(isDragActive ? activeStyle : {}),
                      ...(isDragAccept ? acceptStyle : {}),
                      ...(isDragReject ? rejectStyle : {}),
                      ...(isFocused ? focusStyle : {}),
                    };
                    return (
                      <div {...getRootProps({ style })} className="files-dropzone">
                        <input {...getInputProps()} />
                        <Row>
                          <Col>
                            <i
                              className="ri-upload-cloud-2-line mb-3"
                              style={{ fontSize: 42, color: '#4aa3ff', opacity: 0.8 }}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <h6>Arraste e solte ou clique para buscar</h6>
                          </Col>
                        </Row>
                        {files.length > 0 && (
                          <div className="file-preview">
                            <h6>Arquivos adicionados:</h6>
                            <ul>
                              {files.map((fileWrapper) => (
                                <li key={fileWrapper.props.id} className="file-item">
                                  <span>{fileWrapper.data.name} </span>
                                  <strong
                                    className="text-danger"
                                    role="presentation"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFile(fileWrapper.props.id);
                                    }}
                                  >
                                    Remover
                                  </strong>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  }}
                </Dropzone>
                <strong className="erro-drop text-danger" style={{ display: 'none', fontSize: '16px' }}>
                  Evidência obrigatória
                </strong>
                {extraOptions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={<Checkbox checked={multiAlso} onChange={(e) => setMultiAlso(e.target.checked)} />}
                      label="Esta evidência também aprova outros produtos?"
                    />
                    {multiAlso && (
                      <Box sx={{ pl: 2, maxHeight: 160, overflow: 'auto' }}>
                        {extraOptions.map((p) => (
                          <FormControlLabel
                            key={p.prod_id}
                            control={
                              <Checkbox
                                checked={extraProdIds.includes(p.prod_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setExtraProdIds((prev) => [...prev, p.prod_id]);
                                  } else {
                                    setExtraProdIds((prev) => prev.filter((id) => id !== p.prod_id));
                                  }
                                }}
                              />
                            }
                            label={p.prod_desc || `Produto ${p.prod_id}`}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
                <span className="mt-3">Obervações:</span>
                <TextareaAutosize
                  placeholder="Observações"
                  margin="dense"
                  id="input_obs"
                  name="input_obs"
                  label="Observações"
                  variant="standard"
                  className="p-1"
                  onChange={handleChangeObs}
                />
                <div className="mt-3">
                  <span>Parecer da Revisão:</span>
                  <br />
                  {linksRevisao !== null && linksRevisao.length > 0 ? (
                    linksRevisao.map((link, index) => (
                      <React.Fragment key={link || index}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            downloadFile(
                              `https://oikos.ecoss.is/oikos/${link}`,
                              `${props.produto.prod_desc} Parecer ${index}.xlsx`
                            );
                          }}
                        >
                          {props.produto.prod_desc} Parecer Revisão {index}
                        </a>
                        <br />
                      </React.Fragment>
                    ))
                  ) : linksRevisao === null ? (
                    <Spinner size="sm" />
                  ) : linksRevisao !== null && linksRevisao.length <= 0 ? (
                    <span style={{ fontSize: '12px' }}>Arquivo de revisão interna não encontrado</span>
                  ) : (
                    <></>
                  )}
                </div>
                <a className="mt-3" target="_blank" rel="noopener noreferrer" href={`${props.produto.prod_drive}`}>
                  Link para a pasta do produto
                </a>
                <div className="mt-3">
                  {props.linksProdutos && props.linksProdutos.length !== 0 ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        downloadFile(
                          `https://oikos.ecoss.is/oikos/${props.linksProdutos[props.linksProdutos.length - 1]}`,
                          `${props.produto.prod_desc} REV ${props.linksProdutos.length - 1}.zip`
                        );
                      }}
                    >
                      Download {props.produto.prod_desc} REV {props.linksProdutos.length - 1}
                    </a>
                  ) : props.linksProdutos !== null ? (
                    <span>Procurando arquivo...</span>
                  ) : (
                    <span>Arquivo de produto final não encontrado</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        {wizardPhase === 'form' && (
          <DialogActions>
            {isStatusLoading && (
              <Button autoFocus margin="dense" variant="outlined" disabled endIcon={<Spinner />}>
                Aguarde
              </Button>
            )}
            {!isStatusLoading && (
              <div className="d-flex justify-content-start mt-4">
                <Button autoFocus className="text-success" variant="standard" onClick={aprovaCliente}>
                  Aprovar
                </Button>
                <Button autoFocus className="text-danger" variant="standard" onClick={reprovaCliente}>
                  Reprovar
                </Button>
              </div>
            )}
            <Button onClick={handleClose}>Cancelar</Button>
          </DialogActions>
        )}
      </Dialog>

      <ModalLiberacaoFaturamentoRec
        open={libModalOpen}
        rec={libModalRec}
        onClose={() => {
          if (isStatusLoading) return;
          setLibModalOpen(false);
          setLibModalRec(null);
          setIsStatusLoading(false);
        }}
        onSuccess={onLiberacaoModalSuccess}
      />
    </>
  );
};

ModalFormAprovacaoDoCliente.propTypes = {
  dataContratual: PropTypes.string,
  tecResp: PropTypes.string,
  actual: PropTypes.number,
  orcRubr_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectProducts: PropTypes.array,
  onProductTimelineChanged: PropTypes.func,
};

const baseStyle = {
  cursor: 'pointer',
  padding: '15px 16px 22px 16px',
  borderWidth: 2,
  borderRadius: 6,
  borderColor: '#ccc',
  borderStyle: 'dashed',
  backgroundColor: '#f8f8f8',
  outline: 'none',
  transition: 'all .24s ease-in-out',
  textAlign: 'center',
};

const activeStyle = {
  borderColor: '#4aa3ff',
  color: '#4aa3ff',
};

const focusStyle = {};

const acceptStyle = {
  borderColor: 'green',
  color: 'green',
};

const rejectStyle = {
  borderColor: 'red',
  color: 'red',
};

export default ModalFormAprovacaoDoCliente;
